import { useState } from 'react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import { Textarea } from '@/components/ui/textarea';
import { toast } from '@/components/ui/use-toast';

const MAX_NAME = 200;
const MAX_DESCRIPTION = 2000;

interface CreateGuildChannelDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onCreate: (input: {
    name: string;
    description?: string;
    isPrivate?: boolean;
    isReadOnly?: boolean;
  }) => Promise<string>;
}

/**
 * Admin dialog for creating a new channel inside a guild. Mirrors Discord's
 * "create channel" flow: name, optional description, and two toggles — private
 * (members added explicitly) and read-only (announcement-style).
 */
export function CreateGuildChannelDialog({
  open,
  onOpenChange,
  onCreate,
}: CreateGuildChannelDialogProps) {
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [isPrivate, setIsPrivate] = useState(false);
  const [isReadOnly, setIsReadOnly] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  const reset = () => {
    setName('');
    setDescription('');
    setIsPrivate(false);
    setIsReadOnly(false);
  };

  const handleOpenChange = (next: boolean) => {
    if (!next) reset();
    onOpenChange(next);
  };

  const handleSubmit = async () => {
    const trimmed = name.trim();
    if (!trimmed) return;
    setSubmitting(true);
    try {
      await onCreate({
        name: trimmed,
        description: description.trim() || undefined,
        isPrivate,
        isReadOnly,
      });
      toast({ title: 'Channel created', description: `#${trimmed} is ready.` });
      reset();
      onOpenChange(false);
    } catch (err) {
      toast({
        title: 'Could not create channel',
        description: err instanceof Error ? err.message : 'Please try again.',
        variant: 'destructive',
      });
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Create channel</DialogTitle>
          <DialogDescription>
            Add a new channel to this guild. Members open it in the Chat arche.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 py-2">
          <div className="space-y-1.5">
            <Label htmlFor="guild-channel-name">Channel name</Label>
            <Input
              id="guild-channel-name"
              value={name}
              maxLength={MAX_NAME}
              placeholder="e.g. dev-team"
              onChange={(e) => setName(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter' && !e.shiftKey) {
                  e.preventDefault();
                  // Same guard as the footer button — a second Enter while the
                  // create is in flight must not submit twice.
                  if (!submitting) void handleSubmit();
                }
              }}
              autoFocus
            />
          </div>

          <div className="space-y-1.5">
            <Label htmlFor="guild-channel-description">Description (optional)</Label>
            <Textarea
              id="guild-channel-description"
              value={description}
              maxLength={MAX_DESCRIPTION}
              placeholder="What's this channel for?"
              onChange={(e) => setDescription(e.target.value)}
              rows={2}
            />
          </div>

          <div className="flex items-center justify-between">
            <div className="space-y-0.5">
              <Label htmlFor="guild-channel-private">Private channel</Label>
              <p className="text-xs text-muted-foreground">
                Only invited members and admins can see it.
              </p>
            </div>
            <Switch id="guild-channel-private" checked={isPrivate} onCheckedChange={setIsPrivate} />
          </div>

          <div className="flex items-center justify-between">
            <div className="space-y-0.5">
              <Label htmlFor="guild-channel-readonly">Read-only</Label>
              <p className="text-xs text-muted-foreground">
                Only moderators and above can post (announcements).
              </p>
            </div>
            <Switch
              id="guild-channel-readonly"
              checked={isReadOnly}
              onCheckedChange={setIsReadOnly}
            />
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => handleOpenChange(false)} disabled={submitting}>
            Cancel
          </Button>
          <Button onClick={() => void handleSubmit()} disabled={!name.trim() || submitting}>
            {submitting ? 'Creating…' : 'Create channel'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
